import React, { Fragment, useState } from 'react';
import { ApiAttributeMapping } from '../../api';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { parseErrorMessage } from '../../common/errors/helpers';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { InternalApiEntitySchemaField } from '../../api-internal';
import { useDispatch } from 'react-redux';
import { isChildField, useIndexAndSchemaAreOutOfSyncNotification } from '../helper';
import { SalvButton, SalvModal, SalvTextField, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useMe } from '../../organisation/useMe';
import { useCheckIfPersonsExist } from '../../aml/personActions';
import { useCheckIfTransactionsExist } from '../../aml/transactionActions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';

const validationSchema = Yup.object().shape({
  mapping: Yup.string().required('Required'),
  booleanTrueValue: Yup.string().notRequired(),
  format: Yup.string().notRequired(),
});

export interface MappingsModalProps {
  mapping: ApiAttributeMapping;
  onChange: (values: ApiAttributeMapping) => Promise<void>;
  schemaFields: InternalApiEntitySchemaField[];
}

export const MappingsModal = ({ mapping, onChange, schemaFields }: MappingsModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const { features } = useMe();
  const { data: personsExist } = useCheckIfPersonsExist();
  const { data: transactionsExist } = useCheckIfTransactionsExist();

  const entitiesExist = !!(personsExist || transactionsExist);

  const isMappingEditingRestricted =
    isFeatureEnabled(ORGANISATION_FEATURE.RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS, features) && entitiesExist;

  const { notify: notifyIndexAndSchemaAreOutOfSync } = useIndexAndSchemaAreOutOfSyncNotification();

  const getSchemaFieldsOfAttributeMapping = (fieldName: string): InternalApiEntitySchemaField[] => {
    return schemaFields.filter((field) => field.fieldName === fieldName && !isChildField(field));
  };

  const submit = async (
    values: ApiAttributeMapping,
    { setStatus, setSubmitting }: FormikHelpers<ApiAttributeMapping>
  ) => {
    try {
      const newValues: ApiAttributeMapping = {
        ...values,
        booleanTrueValue: values.booleanTrueValue === '' ? undefined : values.booleanTrueValue,
        format: values.format === '' ? undefined : values.format,
      };

      const schemaFieldsOfAttributeMapping = getSchemaFieldsOfAttributeMapping(values.mapping);
      const nonSearchableSchemaFields = schemaFieldsOfAttributeMapping.filter((field) => !field.searchable);
      if (nonSearchableSchemaFields.length) {
        const displayNames = schemaFieldsOfAttributeMapping.map((field) => "'" + field.displayName + "'").join(', ');
        const schemaFieldMessagePart = nonSearchableSchemaFields.length > 1 ? 'Schema Fields' : 'Schema Field';
        const confirmed = await dispatch(
          showConfirmationModal({
            title: `${schemaFieldMessagePart} will be updated`,
            description: `The ${schemaFieldMessagePart} ${displayNames} will be automatically made searchable for being referenced by this Attribute Mapping`,
          })
        );

        if (!confirmed) {
          setSubmitting(false);
          return;
        }
      }

      await onChange(newValues);

      await notifyIfIndexAndSchemaAreOutOfSync(values.mapping, mapping.mapping);

      setShowModal(false);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  const notifyIfIndexAndSchemaAreOutOfSync = async (newMapping: string, oldMapping: string) => {
    if (newMapping !== oldMapping && entitiesExist) {
      await notifyIndexAndSchemaAreOutOfSync();
    }
  };

  const initialValues = { ...mapping };

  return (
    <Formik onSubmit={submit} validationSchema={validationSchema} initialValues={initialValues}>
      {({ isSubmitting, values, setFieldValue, errors, isValid }: FormikProps<ApiAttributeMapping>) => {
        return (
          <Fragment>
            <SalvButton
              label={'Edit'}
              ariaLabel={`Edit ${mapping.field} Attribute Mapping`}
              onClick={() => setShowModal(true)}
              variant='danger-outline'
              size={Size.SMALL}
            />
            <SalvModal show={showModal} onHide={() => setShowModal(false)}>
              <Form>
                <SalvModal.Header closeButton>Edit {mapping.field.toString()} Attribute Mapping</SalvModal.Header>
                <SalvModal.Body>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <SalvTextField
                        label='Field name'
                        description='The field name that will be mapped to this attribute'
                        value={values.mapping}
                        onChange={(value: string) => setFieldValue('mapping', value)}
                        isInvalid={!!errors.mapping}
                        disabled={isMappingEditingRestricted}
                      />
                      {errors.mapping && <div className={'salv-error-message'}>{errors.mapping}</div>}
                    </div>

                    <div>
                      <SalvTextField
                        label='Boolean true value'
                        description='Value that represents true for boolean fields'
                        value={values.booleanTrueValue || ''}
                        onChange={(value: string) => setFieldValue('booleanTrueValue', value)}
                        isInvalid={!!errors.booleanTrueValue}
                      />
                      {errors.booleanTrueValue && <div className={'salv-error-message'}>{errors.booleanTrueValue}</div>}
                    </div>

                    <div>
                      <SalvTextField
                        label='Format'
                        description='Format pattern for date/time fields (e.g., yyyy-MM-dd)'
                        value={values.format || ''}
                        onChange={(value: string) => setFieldValue('format', value)}
                        isInvalid={!!errors.format}
                      />
                      {errors.format && <div className={'salv-error-message'}>{errors.format}</div>}
                    </div>
                  </div>
                </SalvModal.Body>

                <SalvModal.Footer>
                  {isMappingEditingRestricted && (
                    <div
                      className='text-secondary'
                      style={{ width: '100%', display: 'flex', gap: '4px', alignItems: 'center' }}
                    >
                      <span>Why are some form fields disabled?</span>
                      <SalvTooltipWrapper
                        tooltip={
                          <>
                            Attribute Mapping has limited editing capabilities because Entities have already been
                            uploaded
                          </>
                        }
                      >
                        <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer' />
                      </SalvTooltipWrapper>
                    </div>
                  )}
                  <SalvButton label={'Close'} variant='secondary' onClick={() => setShowModal(false)} />
                  <SalvButton label={'Save'} disabled={isSubmitting || !isValid} type='submit' variant='primary' />
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </Fragment>
        );
      }}
    </Formik>
  );
};

export default MappingsModal;
